import { ViewAccessService } from 'src/app/core/services/view-access.service';
import { PermissionName } from '../enums/permission-name.enum';

export interface ViewPermission {
  permissionName: PermissionName;
  hasPermission: boolean;
}

/**
 * @deprecated Use {@link ViewAccessService.prototype.getHasViewPermission} instead
 */
export class ViewPermissionModel {
  permissions: ViewPermission[];

  constructor(viewPermissions: ViewPermission[]) {
    this.permissions = viewPermissions;
  }

  #findPermission(permissionType: PermissionName): ViewPermission | undefined {
    return this.permissions.find(permission => permission.permissionName === permissionType);
  }

  #hasPermission(permissionType: PermissionName): boolean {
    const permission = this.#findPermission(permissionType);
    return !!permission && permission.hasPermission;
  }

  get hasAnyPermission(): boolean {
    return this.permissions.some(({ hasPermission }) => hasPermission);
  }

  get hasPreAccess(): boolean {
    return this.#hasPermission(PermissionName.PRE_ACCESS);
  }

  get isPREOrHasIndexOwnerAccess(): boolean {
    return this.#hasPermission(PermissionName.PRE_OR_INDEX_OWNER_ACCESS);
  }

  get hasCompleteTaskAccess(): boolean {
    return this.#hasPermission(PermissionName.COMPLETE_TASK_LIST_ACCESS);
  }

  get canSubmitDocument(): boolean {
    return this.#hasPermission(PermissionName.DOCUMENT_PROFILE_SUBMIT);
  }

  get canSubmitTagIndex(): boolean {
    return this.#hasPermission(PermissionName.TAG_PROFILE_SUBMIT);
  }

  get canSubmitInterfaceIndex(): boolean {
    return this.#hasPermission(PermissionName.INTERFACE_PROFILE_SUBMIT);
  }

  get canEditDocument(): boolean {
    return this.#hasPermission(PermissionName.DOCUMENT_PROFILE_LOCKED_EDIT);
  }

  get canAccessCurrentDocument(): boolean {
    return this.#hasPermission(PermissionName.DOCUMENT_PROFILE_CURRENT_ACCESS);
  }

  get canEditInterface(): boolean {
    return this.#hasPermission(PermissionName.INTERFACE_PROFILE_LOCKED_EDIT);
  }

  get canAccessCurrentInterface(): boolean {
    return this.#hasPermission(PermissionName.INTERFACE_PROFILE_CURRENT_ACCESS);
  }

  get canSubmit3D(): boolean {
    return this.#hasPermission(PermissionName.MODEL_3D_PROFILE_SUBMIT);
  }

  get canEdit3D(): boolean {
    return this.#hasPermission(PermissionName.MODEL_3D_PROFILE_LOCKED_EDIT);
  }

  get canAccessCurrent3D(): boolean {
    return this.#hasPermission(PermissionName.MODEL_3D_PROFILE_CURRENT_ACCESS);
  }

  get canWritePRM(): boolean {
    return this.#hasPermission(PermissionName.PRM_WRITE);
  }

  get canImportFullPRM(): boolean {
    return this.#hasPermission(PermissionName.PRM_FULL_IMPORT);
  }

  get canImportDiscPRM(): boolean {
    return this.#hasPermission(PermissionName.PRM_DISC_IMPORT);
  }

  get hasTagAccess(): boolean {
    return this.#hasPermission(PermissionName.ORIGINAL_TAG_ACCESS);
  }

  get canEditIndex(): boolean {
    return this.#hasPermission(PermissionName.LOCKED_INDEX_EDIT);
  }

  get canAccessIndex(): boolean {
    return this.#hasPermission(PermissionName.VIEW_ACCESS);
  }

  get canEditOpenedTransmitalTag(): boolean {
    return this.#hasPermission(PermissionName.TRANSMITTAL_OPEN_TAGS_REMOVE);
  }

  get canEditLockedTransmitalTag(): boolean {
    return this.#hasPermission(PermissionName.TRANSMITTAL_LOCKED_TAGS_REMOVE);
  }

  get toInterface(): ViewPermission[] {
    return this.permissions;
  }

  get hasProjectDashboardAccess(): boolean {
    return this.#hasPermission(PermissionName.PROJECT_DASHBOARD_ACCESS);
  }

  get hasSupplierDashboardAccess(): boolean {
    return this.#hasPermission(PermissionName.SUPPLIER_DASHBOARD_ACCESS);
  }

  get canRevokeFiles(): boolean {
    return this.#hasPermission(PermissionName.DOCUMENTFILE_ADMIN);
  }
}
